import {
  AssetNode,
  AssetType,
  FinishTradeDataType,
  getFinishTradeStatus,
  getTradeContentByTypeName,
  isBpfsAsset,
} from '../../utils';
import { TFunction } from 'i18next';
import {
  getServerLocale,
  initServerTranslation,
} from '@bts-web/utils-lokalise';
import { LegacyFinishTradeKeyClearOnMount } from '../legacy-freeze/LegacyFinishTradeKeyClearOnMount';
import { FinishTradeContentDisplay } from './FinishTradeContentDisplay/FinishTradeContentDisplay';
import { GenericErrorMessage } from '../../../layouts/GenericErrorMessage/GenericErrorMessage';
import { getServerFeatureFlags } from '../../../statsig/sdk/server';

type FinishTradeControllerProps = {
  searchParams: Record<string, string>;
  transactionData: FinishTradeDataType;
  tradeId?: string;
};

export const FinishTradeController = async ({
  searchParams,
  transactionData,
  tradeId,
}: FinishTradeControllerProps) => {
  const { t } = await initServerTranslation();

  const locale = await getServerLocale();

  const { feature_enableFiatTransfers, feature_hasSavingsPlansEnabled } =
    await getServerFeatureFlags([
      'feature_enableFiatTransfers',
      'feature_hasSavingsPlansEnabled',
    ] as const);

  const { asset } = transactionData ?? {
    status: 'FAILED',
  };

  const { id, logoUrl, name } = (asset || {}) as NonNullable<AssetNode>;

  const { hasTaxOfflineWarning, reason } = searchParams ?? {};

  const content = getTradeContentByTypeName({
    translationUtil: t as TFunction,
    locale,
    transactionData,
    hasTaxOfflineWarning: hasTaxOfflineWarning?.toLowerCase() === 'true',
    reason,
  });

  const isBpfs = isBpfsAsset(asset?.__typename as AssetType);

  if (content) {
    // Legacy-only: clear pending order flow key when trade flow ends (SUCCESS/FAILED). Remove with legacy flow. See trade/AccepBackwardsCompatibilityInterceptor/legacy-freeze/README.md
    return (
      <LegacyFinishTradeKeyClearOnMount
        status={getFinishTradeStatus(transactionData)}
      >
        <FinishTradeContentDisplay
          assetId={id}
          assetLogoUrl={logoUrl ?? ''}
          assetName={name ?? ''}
          isSell={transactionData?.__typename === 'SellTransaction'}
          isBuy={transactionData?.__typename === 'BuyTransaction'}
          sellFiatAmount={
            transactionData?.__typename === 'SellTransaction'
              ? transactionData.fiatAmount?.value
              : undefined
          }
          tradeId={tradeId}
          enableFiatTransfers={feature_enableFiatTransfers}
          hasSavingsPlansEnabled={feature_hasSavingsPlansEnabled}
          isBpfs={isBpfs}
          {...content}
        />
      </LegacyFinishTradeKeyClearOnMount>
    );
  } else {
    return <GenericErrorMessage />;
  }
};
